<script setup lang="ts">
import { computed, watch, nextTick, onBeforeUnmount } from 'vue';
import BasicModal from '@/components/display/general/BasicModal.vue';
import ArticlePanel from '@/components/article/ArticlePanel.vue';
import OutlinePanel from '@/components/outline/OutlinePanel.vue';
import { dragElement } from '@/assets/js/utils';
import UIkit from 'uikit';

type Mode = 'article' | 'outline';

interface Article {
  [key: string]: unknown;
}

interface Outline {
  [key: string]: unknown;
}

interface Props {
  mode?: Mode | null;
  article?: Article;
  outline?: Outline;
  open?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  mode: null,
  article: () => ({}),
  outline: () => ({}),
  open: false,
});

const modeValue = computed(() => props.mode ?? '');
const hasMode = computed(() => modeValue.value.length > 0);

const modalRef = computed(() => (hasMode.value ? `${modeValue.value}FullViewModal` : undefined));
const modalId = computed(() => (hasMode.value ? `${modeValue.value}FullViewModal` : undefined));
const modalElementId = computed(() => (hasMode.value ? `${modeValue.value}FullModal` : undefined));
const dragModalId = computed(() => {
  if (!hasMode.value) {
    return undefined;
  }
  const mode = modeValue.value;
  return `fullContext${mode.charAt(0).toUpperCase()}${mode.slice(1)}ModalId`;
});

watch(
  () => props.open,
  newVal => {
    if (newVal && hasMode.value) {
      nextTick(() => {
        setupDragAndShow();
      });
    }
  }
);

const setupDragAndShow = () => {
  if (!hasMode.value || !modalElementId.value || !modalId.value) {
    return;
  }
  // Setup drag functionality
  dragElement(document.getElementById(modalElementId.value), dragModalId.value);

  // Show the modal
  UIkit.modal(document.getElementById(modalId.value)).show();
};

const noop = () => undefined;

const getModalElement = () => (modalId.value ? document.getElementById(modalId.value) : null);

const registerHideListener = (handler: () => void) => {
  const element = getModalElement();
  if (!element || typeof handler !== 'function') {
    return noop;
  }

  const util = UIkit?.util;
  if (!util || typeof util.on !== 'function' || typeof util.off !== 'function') {
    handler();
    return noop;
  }

  const wrapped = () => {
    handler();
    util.off(element, 'hidden', wrapped);
  };

  util.on(element, 'hidden', wrapped);

  return () => util.off(element, 'hidden', wrapped);
};

const createHideListener = (handler: () => void) => {
  if (!hasMode.value) {
    return noop;
  }

  if (getModalElement()) {
    return registerHideListener(handler);
  }

  let cleanup: () => void = noop;

  nextTick(() => {
    cleanup = registerHideListener(handler);
  });

  return () => cleanup();
};

const activeCleanups = new Set<() => void>();

const trackCleanup = (cleanup: () => void) => {
  activeCleanups.add(cleanup);
  return () => {
    cleanup();
    activeCleanups.delete(cleanup);
  };
};

watch(
  () => props.open,
  openVal => {
    if (!openVal) {
      activeCleanups.forEach(cleanup => cleanup());
      activeCleanups.clear();
    }
  }
);

onBeforeUnmount(() => {
  activeCleanups.forEach(cleanup => cleanup());
  activeCleanups.clear();
});

const onHide = (handler: () => void) => trackCleanup(createHideListener(handler));

defineExpose({
  onHide,
  getModalElement,
});
</script>

<template>
  <div v-if="open && mode" uk-modal :ref="modalRef" :id="modalId">
    <BasicModal
      class="fullview moveableBasis"
      :showClose="true"
      :heading="{ title: 'Full Context', bgColor: '#2C3157', color: 'white' }"
      :id="modalElementId"
      :modalId="dragModalId"
    >
      <ArticlePanel
        v-if="mode === 'article'"
        class="childFullView"
        :article="article"
        :hideArrow="true"
        :childFullView="true"
      />

      <OutlinePanel
        v-if="mode === 'outline'"
        class="childFullView"
        :isFullView="true"
        :outlineSource="outline"
      />
    </BasicModal>
  </div>
</template>
